import { findParentNodeClosestToPos } from "@tiptap/core";

import {
  collapsedBlockInDirection,
  collapsedRunSelection,
} from "@/components/tiptap/collapse/blockCollapse";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { moveUpDown } from "@/models/cursor/moveUpDown";

import type { MoveToOptions } from "@/models/cursor/moveUpDown";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";

/**
 * Jump up/down between the lines.
 * It will go up or down from the provided coordinates until it find the matching line to jump.
 *
 */
export function jumpLine(
  direction: "up" | "down",
  { left, top }: { left: number; top: number },
  doc: DocumentModel,
): boolean {
  const { tiptap } = doc.editor;
  if (tiptap) {
    const collapsedPos =
      doc.selection.isKeyboardSelecting || !tiptap.view.endOfTextblock(direction)
        ? null
        : collapsedBlockInDirection(tiptap.view, direction);

    if (collapsedPos !== null) {
      doc.cursor.reset();
      const selection = collapsedRunSelection(tiptap.view, collapsedPos);
      return tiptap
        .chain()
        .command(({ tr, dispatch }) => {
          if (dispatch) {
            tr.setSelection(selection).scrollIntoView();
          }
          return true;
        })
        .focus()
        .run();
    }

    const target = findJumpLineTarget(direction, { left, top }, doc);
    if (!target) {
      return false;
    }
    if ("nodePos" in target) {
      doc.cursor.reset();
      const node = tiptap.state.doc.nodeAt(target.nodePos);
      if (node?.isAtom && node?.isBlock) {
        return tiptap.commands.setNodeSelection(target.nodePos);
      }
      return tiptap.commands.focus(target.nodePos);
    }
    if ("expressionPos" in target) {
      return moveUpDown(doc, direction, { ...target.coords, allowCurrent: true });
    }
  }
  return false;
}

export function findSelectionCell(pos: number, editor: Editor) {
  const SELECTION_BOUNDARIES_NODES = ["tableCell", "tableHeader"];
  return findParentNodeClosestToPos(editor.state.doc.resolve(pos), (node) =>
    SELECTION_BOUNDARIES_NODES.includes(node.type.name),
  );
}

function findCellLimitRect(pos: number, editor: Editor): DOMRect | undefined {
  const cell = findSelectionCell(pos, editor);
  if (cell) {
    const domNode = editor.view.nodeDOM(cell.pos);
    if (domNode instanceof HTMLElement) {
      return domNode.getBoundingClientRect();
    }
  }
}

function isInRect(offsetTop: number, rect: DOMRect | undefined): boolean {
  if (!rect) {
    return true;
  }
  return offsetTop >= rect.top && offsetTop <= rect.bottom;
}

export function findJumpLineTarget(
  direction: "up" | "down",
  { left, top }: { left: number; top: number },
  doc: DocumentModel,
  limitByCell?: boolean,
): { nodePos: number } | { expressionPos: number; coords: MoveToOptions } | undefined {
  const LINE_OFFSET = 5;
  const LINE_MAX_OFFSET = 1000;

  const { tiptap } = doc.editor;
  if (tiptap) {
    const initialPos = tiptap.view.posAtCoords({ left, top });
    const initialNode = initialPos ? tiptap.state.doc.nodeAt(initialPos.pos) : null;
    const isLeftToShortNode = initialNode?.inlineContent;

    if (initialPos) {
      const limitRect = limitByCell ? findCellLimitRect(initialPos.pos, tiptap) : undefined;

      const offset = direction === "up" ? -LINE_OFFSET : LINE_OFFSET;
      let coords: MoveToOptions = { left, top };

      for (
        let offsetTop = top + offset;
        Math.abs(offsetTop - top) <= LINE_MAX_OFFSET && isInRect(offsetTop, limitRect);
        offsetTop += offset
      ) {
        coords = { left, top: offsetTop };
        const newPos = tiptap.view.posAtCoords(coords);

        if (
          newPos &&
          newPos.inside !== -1 &&
          ((newPos.pos !== initialPos.pos && newPos.pos !== initialPos.inside) ||
            (isLeftToShortNode && newPos.pos === initialPos.pos))
        ) {
          let nodePos: number | null = newPos.pos;
          let node = tiptap.state.doc.nodeAt(nodePos);
          const parentNode = tiptap.state.doc.nodeAt(newPos.inside);

          // If node or parentNode is expression, check that it is not the same expression.
          // There is custom logic to jump to the correct place inside the expression below.
          if (nodeIsExpression(parentNode)) {
            if (newPos.inside === initialPos.inside) {
              node = null;
              nodePos = null;
            } else {
              node = parentNode;
              nodePos = newPos.inside;
            }
          } else if (nodeIsExpression(node)) {
            if (newPos.inside === initialPos.inside) {
              node = null;
              nodePos = null;
            }
          }
          // We are to the right to the shorter line
          else if (parentNode?.inlineContent) {
            if (!node) {
              node = tiptap.state.doc.nodeAt(nodePos - 1);
              if (nodeIsExpression(node)) {
                nodePos -= 1;
              }
            }
          }
          // We are to the left to the shorter line. Find che child of this node closer to the current
          // offsetTop for the case if there are multiple lines in this block node.
          else if (node?.inlineContent) {
            const children = childRanges(node, nodePos, tiptap);
            children.some(({ pos, coords: c, node: n }) => {
              if (c.top <= offsetTop && c.bottom >= offsetTop && (c.top > top || c.bottom < top)) {
                if (pos !== initialPos.pos) {
                  nodePos = pos;
                  node = n;
                }
                return true;
              }
              return false;
            });
            // Atom block nodes (HR, images, etc.) — keep nodePos so we can jump to them
          } else if (node?.isAtom && node?.isBlock) {
            // nodePos is valid; handled below via setNodeSelection in jumpLine()
          } else if (node?.type.name === "tableRow") {
            // use native navigation for tables
            return undefined;
          } else {
            nodePos = null;
            node = null;
          }

          // We found a nodePos were to jump.
          if (nodePos) {
            // Atom nodes: coordsAtPos returns a thin band, not the full node box,
            // so skip the bounds check — posAtCoords already confirmed we're on the node.
            const isAtomNode = node?.isAtom && node?.isBlock;
            const c = tiptap.view.coordsAtPos(nodePos);
            // Check that it is in the correct top offset, otherwise keep searching.
            if (isAtomNode || (c.top <= offsetTop && c.bottom >= offsetTop)) {
              // If the node is set and it is an expression, jump inside. Otherwise use TipTap focus
              // to the position.
              if (node) {
                let expressionPos: number | undefined = undefined;
                // If node at the new position is an expression, put cursor there
                if (nodeIsExpression(node)) {
                  expressionPos = nodePos;
                }
                // Cursor get right after the expression, still a hit
                else if (nodeIsExpression(tiptap.state.doc.nodeAt(nodePos - 1))) {
                  expressionPos = nodePos - 1;
                }

                if (expressionPos) {
                  const wrapper = tiptap.view.nodeDOM(expressionPos) as HTMLElement | undefined;
                  if (wrapper) {
                    const wrapperRect = wrapper.getBoundingClientRect();
                    // Make sure that the left coordinate inside of the wrapper, otherwise we won't
                    // find a cursor there.
                    coords.left = Math.min(
                      Math.max(coords.left, wrapperRect.left),
                      wrapperRect.right,
                    );
                    coords.wrapper = wrapper;
                  }
                  return { expressionPos, coords };
                }
              }

              return { nodePos };
            }
          }
        }
      }
    }
  }
}

type ChildRanges = {
  pos: number;
  node: Node;
  coords: { top: number; bottom: number };
};

export function childRanges(parent: Node, parentPos: number, tiptap: Editor): ChildRanges[] {
  const base = parentPos === -1 ? 0 : parentPos + 1; // doc uses 0, others use +1
  const out: ChildRanges[] = [];
  let pos = base;

  for (let i = 0; i < parent.childCount; i++) {
    const child = parent.child(i);
    const { top, bottom } = tiptap.view.coordsAtPos(pos);
    out.push({
      pos,
      node: child,
      coords: { top, bottom },
    });
    pos += child.nodeSize;
  }
  return out;
}
